/**
 * funnel-graph.ts — ОДИН общий вычислитель графа воронки (WEB-1807).
 *
 * ВНИМАНИЕ: файл существует в ДВУХ экземплярах и обязан быть побайтово
 * одинаковым в обоих:
 *   backend/src/ai-funnels/funnel-graph.ts
 *   frontend/src/logic-engine/funnel-graph.ts
 * За равенством следят сторожа `funnel-graph.parity.spec.ts` (бэк) и
 * `funnelGraph.parity.test.ts` (фронт) — они краснеют в обе стороны. Поэтому
 * в файле НЕТ НИ ОДНОГО импорта: всё, что зависит от стороны (резолвер живого
 * списка вариантов, константы реестра ссылочных полей), приходит параметрами.
 *
 * ЗАЧЕМ. Ветвление в продукте делают два механизма, и ни один не знал слова
 * «ветка»: правила `Rule[]` (условный переход) и `nextScreenId` у варианта
 * ответа / кнопки «Далее» (безусловный). Обход графа был написан дважды и оба
 * раза наполовину: `funnel-graph-validation.ts` (знал правила, не знал
 * достижимости) и `funnel-reachability.util.ts` (наоборот). Третью копию
 * писать нельзя — класс дефекта «разошедшаяся копия правила». Оба обхода
 * теперь тонкие обёртки над этим модулем.
 *
 * КАНОН ПЕРЕХОДА — лестница плеера
 * (`frontend/src/pages/navigation/navigationStateMachine.ts:130-192`):
 *   1) правило (`Rule.action` GOTO_SCREEN/GOTO_ENDING; GOTO_PAYWALL — конец
 *      квиза, переход к пейволлу воронки без экрана-цели, WEB-3819);
 *   2) `option.nextScreenId` — у одиночного выбора: `single_choice` и
 *      `image_choice` без множественного режима (D6 ратифицирован для
 *      single_choice; на image_choice расширен решением владельца 22.10.2026
 *      ~22:40 — «выбор картинкой может вести на разные экраны по ответу»,
 *      РЕШЕНИЯ.md разборщика, 2a26129). Множество типов — ОДНО,
 *      `OPTION_NAV_BLOCK_TYPES` ниже: его читают плеер, панель, граф
 *      редактора и сторожа двух концов;
 *   3) `button_next.nextScreenId` — первый блок «Далее» экрана, непустое;
 *   4) следующий экран по порядку.
 * Ступени 1-2 — УСЛОВНЫЕ рёбра (зависят от ответа/условия), 3-4 —
 * безусловные. Экран с двумя и более разными условными целями = РАЗВИЛКА.
 *
 * Модуль отдаёт ФАКТЫ (все заявленные ссылки с пометкой источника и признаком
 * «исполняет ли это плеер»), а не одно мнение: у сегодняшних потребителей
 * набор рёбер шире плеерского (проверка публикации считает ссылкой поле
 * `nextScreenId` на ЛЮБОМ блоке, проверка достижимости — переход у варианта
 * любого типа блока), и сузить его здесь значило бы поменять их поведение.
 *
 * Модуль чистый: без БД, DOM, сети; на мусорном вводе не бросает.
 */

/** Откуда взялась ссылка/ребро. */
export type FunnelRefSource =
  | 'rule' // правило: Rule.action.target / Rule.scope_ref / Condition.source_ref
  | 'option' // вариант ответа: option.nextScreenId
  | 'button_next' // кнопка «Далее»: nextScreenId на блоке
  | 'branch' // легаси conditional_branch.branchNextScreenId (плеер не читает)
  | 'order'; // неявный переход на следующий экран по порядку

/**
 * Одна заявленная ссылка. Ссылки НАВИГАЦИОННЫЕ (`navigational: true`) — это
 * рёбра графа, когда цель существует (`resolved`). Ссылки правил на блок
 * (`Rule.scope_ref`, `Condition.source_ref`) навигационными не являются:
 * рёбер не создают, но их разрыв так же молча выключает ветвление.
 */
export interface FunnelRef {
  source: FunnelRefSource;
  /** Имя поля — так же, как его называют предупреждения публикации. */
  field: string;
  /**
   * Узел, НА КОТОРОМ ссылка объявлена: id экрана для полей содержимого,
   * `scope_ref` (экран ИЛИ блок) либо '(logic)' для правил.
   */
  from: string;
  /** Экран-владелец: для правила элемента — экран его блока; null, если неизвестен. */
  fromScreenId: string | null;
  /**
   * Существующий экран-цель, либо null (висячая или ненавигационная ссылка;
   * найденное «к результату» в сам экран правила — конец квиза, WEB-3491).
   */
  to: string | null;
  /** Значение поля как оно лежит в контенте (может быть пустой строкой). */
  declared: unknown;
  /** Цель найдена: экран — для навигационных, экран ИЛИ блок — для ссылок правил. */
  resolved: boolean;
  navigational: boolean;
  /** Переход зависит от ответа/условия (правило, вариант ответа). */
  conditional: boolean;
  /** Исполняет ли этот переход плеер (лестница выше; D6 для вариантов). */
  honoredByPlayer: boolean;
  blockId: string | null;
  blockType: string | null;
  optionId: string | null;
  /**
   * МЕСТО варианта в списке блока (0-based), null — ссылка не от варианта.
   *
   * Ревизия пачки 31 (находка 9): у варианта может не быть собственного id —
   * его выдаёт `remapIds` в режиме починки, СВЕЖИМ uuid на каждом ходе. Всё,
   * что сравнивает два состояния одной воронки (отпечаток графа переходов),
   * обязано опознавать вариант по месту, иначе выдача идентификатора сама
   * выглядит изменением ветвления.
   */
  optionIndex: number | null;
  ruleId: string | null;
  /**
   * WEB-3819: конечное ребро «к пейволлу воронки» (правило `GOTO_PAYWALL`).
   * Экрана-цели в этой части нет (`to` = null), ссылка найдена
   * (`resolved`): плеер здесь заканчивает квиз и показывает то, что идёт
   * после квиза. У остальных ссылок поля нет.
   */
  toPaywall?: true;
}

/** Разрыв ссылки — форма предупреждения публикации (WEB-176/WEB-1491). */
export interface FunnelDanglingRef {
  field: string;
  fromScreen: string;
  value: string;
}

export interface FunnelGraphScreen {
  id: string;
  index: number;
  /** Человеческое имя экрана (`name`/`title`), если есть. */
  name: string | null;
  blockIds: string[];
}

/** Экран, из которого расходятся ≥2 РАЗНЫХ условных перехода. */
export interface FunnelFork {
  screenId: string;
  /** Цели условных переходов, в порядке объявления, без повторов. */
  targets: string[];
  /** Идентификаторы веток (= id головного экрана каждой ветки). */
  branchIds: string[];
}

/** Линейный хвост от развилки до слияния (или до конца воронки). */
export interface FunnelBranch {
  /** Идентификатор ветки = id её головного экрана. */
  id: string;
  forkScreenId: string;
  /** Имя ветки для человека: имя головного экрана, иначе его id. */
  name: string;
  /** Экраны ветки по порядку прохождения, головной первым. */
  screenIds: string[];
  /** Экран, в который ветка уводит (слияние, соседняя ветка), либо null. */
  exitScreenId: string | null;
  /**
   * Чем уводит: 'rule' / 'option' / 'button_next' — свой (явный) выход
   * последнего экрана ветки, 'order' — провал по порядку.
   */
  exitSource: FunnelRefSource | null;
  /** Хвост без явного выхода, проваливающийся в соседнюю ветку. */
  isDeadEnd: boolean;
  /** В какую ветку проваливается (только для тупика). */
  fallsIntoBranchId: string | null;
}

export interface FunnelGraphResult {
  screens: FunnelGraphScreen[];
  screenIds: string[];
  refs: FunnelRef[];
  danglingRefs: FunnelDanglingRef[];
  /** Каждый цикл — упорядоченный список экранов навигационной петли. */
  cycles: string[][];
  forks: FunnelFork[];
  branches: FunnelBranch[];
  deadEndBranches: FunnelBranch[];
  /** Экраны, до которых нет пути от первого экрана. */
  unreachableScreenIds: string[];
}

export interface FunnelGraphInput {
  /** Содержимое воронки (jsonb-массив экранов) — любой формы. */
  screens: unknown;
  /** Правила `Rule[]` (колонка logic/draft_logic), если есть. */
  rules?: unknown;
  /**
   * Живой список вариантов ответа блока — ОБЯЗАТЕЛЬНЫЙ параметр.
   *
   * У списка две номенклатуры (реестр пар имён): у части типов блоков кабинет
   * пишет своё префиксное имя, генерация — короткое. Поэтому имя поля здесь
   * НЕ ЗАШИТО и умолчания у резолвера нет: своя строка `options` в общем
   * обходчике — ровно тот дефект, от которого стоит сторож WEB-1690
   * (картиночный выбор молча выпадал из-под проверки графа). Бэк передаёт
   * `resolveBlockOptions` (persistent-id/reference-fields), фронт — своё
   * чтение парного поля (`readPairedField`, blockFieldNamePairs).
   */
  resolveOptions: (block: unknown) => unknown[];
  /** Потолок вложенности групп условий; бэк передаёт значение реестра. */
  maxConditionGroupDepth?: number;
  /** Источник условия, у которого `source_ref` — id блока (реестр: 'ANSWER'). */
  answerConditionSource?: string;
  /** Типы действий правила, чей `target` — id экрана (реестр). */
  gotoActionTypes?: readonly string[];
}

const DEFAULT_MAX_CONDITION_GROUP_DEPTH = 10;
const DEFAULT_ANSWER_CONDITION_SOURCE = 'ANSWER';
const DEFAULT_GOTO_ACTION_TYPES: readonly string[] = [
  'GOTO_SCREEN',
  'GOTO_ENDING',
];
/** Тип блока кнопки «Далее» — ступень 3 лестницы плеера. */
const BUTTON_NEXT_BLOCK_TYPE = 'button_next';
/**
 * Типы блоков, у вариантов которых плеер исполняет переход (ступень 2, D6 +
 * решение владельца 22.10.2026). Одно множество на всю систему: плеер
 * (`navigationStateMachine.ts`), панель и граф редактора
 * (`nextScreenTargets.ts`) и сторожа читают его отсюда, своих списков нет.
 */
export const OPTION_NAV_BLOCK_TYPES: ReadonlySet<string> = new Set([
  'single_choice',
  'image_choice',
]);

/**
 * Исполняет ли плеер переход у вариантов ЭТОГО блока: тип из
 * `OPTION_NAV_BLOCK_TYPES` и выбор одиночный — у выбора картинкой в
 * множественном режиме (`imageChoiceMultiple` кабинета, `multiple`
 * генерации; имя кабинета первым) выбор не закончен, как у multiple_choice.
 */
export function blockHonorsOptionNav(block: unknown): boolean {
  if (!isObject(block) || typeof block.type !== 'string') return false;
  if (!OPTION_NAV_BLOCK_TYPES.has(block.type)) return false;
  if (block.type === 'image_choice') {
    return (block.imageChoiceMultiple ?? block.multiple) !== true;
  }
  return true;
}

type Json = Record<string, unknown>;

function isObject(value: unknown): value is Json {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function humanName(screen: Json): string | null {
  for (const field of ['name', 'title']) {
    const value = screen[field];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
}

/**
 * Циклы навигации — обход в глубину по обратным рёбрам. Возвращает по одному
 * представителю на каждый различный цикл (сравнение по набору участников).
 * Порядок обхода = порядок появления узлов в переданной карте рёбер.
 */
export function detectFunnelCycles(
  edges: Map<string, Set<string>>,
): string[][] {
  const WHITE = 0;
  const GRAY = 1;
  const BLACK = 2;
  const color = new Map<string, number>();
  const stack: string[] = [];
  const cycles: string[][] = [];
  const seen = new Set<string>();

  function dfs(node: string): void {
    color.set(node, GRAY);
    stack.push(node);
    for (const next of edges.get(node) ?? []) {
      const c = color.get(next) ?? WHITE;
      if (c === GRAY) {
        const idx = stack.indexOf(next);
        const cycle = stack.slice(idx);
        const key = [...cycle].sort().join(',');
        if (!seen.has(key)) {
          seen.add(key);
          cycles.push(cycle);
        }
      } else if (c === WHITE) {
        dfs(next);
      }
    }
    stack.pop();
    color.set(node, BLACK);
  }

  for (const node of edges.keys()) {
    if ((color.get(node) ?? WHITE) === WHITE) dfs(node);
  }
  return cycles;
}

/** Операторы, у которых пустое значение условия = незаполненное условие. */
const OPERATORS_REQUIRING_NONEMPTY_VALUE: readonly string[] = [
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'begins_with',
  'ends_with',
];

/**
 * Сработает ли условие по ответу, КОТОРОГО НЕТ (блок удалён или не
 * существовал), — так, как его решит движок правил (WEB-3123).
 *
 * Движок есть только в кабинете (`logic-engine/evaluate.ts` →
 * `evaluateCondition`, операторы — `logic-engine/operators.ts`), а этот модуль
 * без импортов (побайтовое зеркало на сервере). Поэтому здесь КОПИЯ решения
 * движка для одного случая — ответа нет. Совпадение с движком на каждом
 * операторе схемы и наборе значений держит сторож
 * `frontend/src/logic-engine/__tests__/funnel-graph.web3123-engine-parity.test.ts`.
 *
 * Как решает движок: незаполненное условие (пустой `source_ref`, пустое
 * значение у сравнения строк) — никогда; без ответа левая часть — пустая
 * строка: «ответ пуст» — да, «не пуст» и числа — нет, сравнение строк — по
 * значению. Значение-переменная ({var_ref}) заранее неизвестно: «может
 * сработать», если оператор вообще зависит от значения.
 */
export function answerlessConditionMayFire(condition: unknown): boolean {
  if (!isObject(condition)) return false;
  const sourceRef = condition.source_ref;
  if (typeof sourceRef !== 'string' || sourceRef.trim() === '') return false;
  const operator = condition.operator;
  if (typeof operator !== 'string') return false;
  const value = condition.value;
  if (
    OPERATORS_REQUIRING_NONEMPTY_VALUE.includes(operator) &&
    typeof value === 'string' &&
    value.trim() === ''
  ) {
    return false;
  }
  if (operator === 'is_empty') return true;
  if (operator === 'is_not_empty') return false;
  if (
    operator === 'gt' ||
    operator === 'gte' ||
    operator === 'lt' ||
    operator === 'lte'
  ) {
    return false;
  }
  const isVariable = isObject(value) && typeof value.var_ref === 'string';
  const norm = (v: unknown): string =>
    (v === null || v === undefined ? '' : String(v)).trim().toLowerCase();
  // Правая часть, как её получит движок: строка или список.
  const rhs: unknown =
    typeof value === 'string' || Array.isArray(value) ? value : '';
  switch (operator) {
    case 'equals':
    case 'contains':
    case 'begins_with':
    case 'ends_with':
      return isVariable || norm(rhs) === '';
    case 'not_equals':
    case 'not_contains':
      return isVariable || norm(rhs) !== '';
    case 'is_one_of': {
      if (isVariable) return true;
      const list: unknown[] = Array.isArray(rhs) ? rhs : [rhs];
      return list.some((item) => norm(item) === '');
    }
    default:
      return false;
  }
}

/** Действие «перейти к результату»: цель у него необязательна (плеер). */
const GOTO_ENDING_ACTION_TYPE = 'GOTO_ENDING';
/** Блок экрана результата — запасная цель «перейти к результату». */
const RESULT_SCREEN_BLOCK_TYPE = 'result_screen';

/**
 * Имя экрана для сравнения: без регистра и без пробелов — ровно как у плеера
 * (`navigationStateMachine.ts`, `normScreenName`) и его приведения ссылок при
 * загрузке (`logic-engine/resolveScreenRefs.ts`, `norm`).
 */
function normScreenName(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '');
}

/**
 * Экран, который автор назвал целью: по коду, иначе по имени (`name`, как у
 * плеера — не `title`). null — такого экрана нет.
 */
function findScreenByCodeOrName(
  screens: readonly Json[],
  target: string,
): string | null {
  const byId = screens.find((s) => s.id === target);
  if (byId) return byId.id as string;
  const wanted = normScreenName(target);
  if (wanted.length === 0) return null;
  const byName = screens.find(
    (s) =>
      typeof s.name === 'string' &&
      s.name.trim().length > 0 &&
      normScreenName(s.name) === wanted,
  );
  return byName ? (byName.id as string) : null;
}

/**
 * Куда уведёт «перейти к результату» (WEB-3486) — тем же порядком, что у
 * плеера (`navigationStateMachine.ts`, `resolveEndingScreen`, и приведение
 * имён при загрузке `resolveScreenRefs`): код экрана → имя экрана → первый
 * экран с блоком `result_screen` → последний экран. Цели нет, она пустая или
 * не нашлась — запаски (плеер выдуманный код наружу не отдаёт, WEB-1717).
 * null — экранов нет. Совпадение с настоящим плеером держит сторож
 * `frontend/src/logic-engine/__tests__/funnel-graph.web3123-engine-parity.test.ts`.
 *
 * `screens` — экраны с непустым строковым id, в порядке воронки; `named` —
 * цель, уже найденная кодом или именем (`findScreenByCodeOrName`, WEB-3491:
 * одна находка на правило для обоих переходов), null — не нашлась или её нет.
 */
function resolveEndingScreenId(
  screens: readonly Json[],
  named: string | null,
): string | null {
  if (named !== null) return named;
  const resultScreen = screens.find(
    (s) =>
      Array.isArray(s.blocks) &&
      s.blocks.some((b) => isObject(b) && b.type === RESULT_SCREEN_BLOCK_TYPE),
  );
  if (resultScreen) return resultScreen.id as string;
  const last = screens[screens.length - 1];
  return last ? (last.id as string) : null;
}

/** Карта рёбер (from → множество to) из подмножества ссылок. */
export function edgeMapFromRefs(refs: FunnelRef[]): Map<string, Set<string>> {
  const edges = new Map<string, Set<string>>();
  for (const ref of refs) {
    if (!ref.resolved || ref.to === null) continue;
    if (!edges.has(ref.from)) edges.set(ref.from, new Set());
    edges.get(ref.from)!.add(ref.to);
  }
  return edges;
}

/**
 * Пустой результат СОБИРАЕТСЯ ЗАНОВО на каждый вызов: общий на модуль объект
 * означал бы, что списки внутри — одни и те же массивы у всех вызывающих, и
 * первая же правка результата у одного потребителя молча меняла бы ответ
 * другому.
 */
function emptyResult(): FunnelGraphResult {
  return {
    screens: [],
    screenIds: [],
    refs: [],
    danglingRefs: [],
    cycles: [],
    forks: [],
    branches: [],
    deadEndBranches: [],
    unreachableScreenIds: [],
  };
}

/** Параметры одной навигационной ссылки (см. `FunnelRef`). */
interface NavRefDraft {
  source: FunnelRefSource;
  field: string;
  from: string;
  declared: unknown;
  conditional: boolean;
  honoredWhenResolved: boolean;
  blockId?: string | null;
  blockType?: string | null;
  optionId?: string | null;
  optionIndex?: number | null;
  ruleId?: string | null;
  fromScreenId?: string | null;
  /**
   * Цель уже разрешена вызывающим (WEB-3486, «перейти к результату»: имя и
   * запаски плеера). Не задано — цель = `declared`, если такой экран есть.
   */
  resolvedTo?: string | null;
}

/**
 * Первые два этапа обхода: перепись экранов и блоков, ссылки содержимого
 * (кнопка «Далее», варианты, легаси-переход) и неявные рёбра по порядку.
 * Общая часть полного графа (`buildFunnelGraph`) и лёгкого графа посетителя
 * (`buildFunnelVisitorGraph`) — одна, чтобы вторая копия не разошлась.
 */
function collectFunnelContent(
  rawScreens: unknown[],
  resolveOptions: (block: unknown) => unknown[],
) {
  // ── Перепись экранов и блоков ────────────────────────────────────────────
  const screenIds = new Set<string>();
  const blockIds = new Set<string>();
  const blockOwner = new Map<string, string>();
  const screens: FunnelGraphScreen[] = [];
  const screenEntries: Json[] = [];

  for (const raw of rawScreens) {
    if (!isObject(raw)) continue;
    const id = raw.id;
    const blocks = Array.isArray(raw.blocks) ? raw.blocks : [];
    const ownBlockIds: string[] = [];
    for (const block of blocks) {
      if (!isObject(block)) continue;
      if (typeof block.id === 'string' && block.id) {
        blockIds.add(block.id);
        ownBlockIds.push(block.id);
        if (typeof id === 'string' && id && !blockOwner.has(block.id)) {
          blockOwner.set(block.id, id);
        }
      }
    }
    if (typeof id !== 'string' || !id) continue;
    screenIds.add(id);
    screens.push({
      id,
      index: screens.length,
      name: humanName(raw),
      blockIds: ownBlockIds,
    });
    screenEntries.push(raw);
  }

  const screenById = new Map<string, FunnelGraphScreen>();
  for (const screen of screens) {
    if (!screenById.has(screen.id)) screenById.set(screen.id, screen);
  }

  // ── Ссылки содержимого ───────────────────────────────────────────────────
  const refs: FunnelRef[] = [];

  function pushNavRef(ref: NavRefDraft): FunnelRef {
    const declared = ref.declared;
    const target =
      ref.resolvedTo !== undefined
        ? ref.resolvedTo
        : typeof declared === 'string' &&
            declared.length > 0 &&
            screenIds.has(declared)
          ? declared
          : null;
    const resolved = target !== null;
    const pushed: FunnelRef = {
      source: ref.source,
      field: ref.field,
      from: ref.from,
      fromScreenId:
        ref.fromScreenId === undefined
          ? screenIds.has(ref.from)
            ? ref.from
            : null
          : ref.fromScreenId,
      to: target,
      declared,
      resolved,
      navigational: true,
      conditional: ref.conditional,
      honoredByPlayer: resolved && ref.honoredWhenResolved,
      blockId: ref.blockId ?? null,
      blockType: ref.blockType ?? null,
      optionId: ref.optionId ?? null,
      optionIndex: ref.optionIndex ?? null,
      ruleId: ref.ruleId ?? null,
    };
    refs.push(pushed);
    return pushed;
  }

  for (const raw of rawScreens) {
    // Тот же предикат, что у проверки публикации: экран с пустым id ссылки
    // всё равно объявляет, и их разрыв обязан быть виден.
    if (!isObject(raw) || typeof raw.id !== 'string') continue;
    const from = raw.id;
    const blocks = Array.isArray(raw.blocks) ? raw.blocks : [];
    let firstButtonNextSeen = false;
    for (const block of blocks) {
      if (!isObject(block)) continue;
      const blockId = typeof block.id === 'string' ? block.id : null;
      const blockType = typeof block.type === 'string' ? block.type : null;
      const isButtonNext = blockType === BUTTON_NEXT_BLOCK_TYPE;
      // Ступень 3 лестницы: плеер берёт ПЕРВЫЙ блок «Далее» экрана и только
      // его — второй такой блок рёбер плеера не даёт (см. tier 3).
      const isPlayerButton = isButtonNext && !firstButtonNextSeen;
      if (isButtonNext) firstButtonNextSeen = true;
      if (typeof block.nextScreenId === 'string') {
        pushNavRef({
          source: 'button_next',
          field: 'button_next.nextScreenId',
          from,
          declared: block.nextScreenId,
          conditional: false,
          honoredWhenResolved: isPlayerButton,
          blockId,
          blockType,
        });
      }
      if (typeof block.branchNextScreenId === 'string') {
        pushNavRef({
          source: 'branch',
          field: 'conditional_branch.branchNextScreenId',
          from,
          declared: block.branchNextScreenId,
          conditional: false,
          // Легаси-блок: плеер его не читает (грамматика правил заменила).
          honoredWhenResolved: false,
          blockId,
          blockType,
        });
      }
      const blockOptions = resolveOptions(block);
      for (
        let optionIndex = 0;
        optionIndex < blockOptions.length;
        optionIndex++
      ) {
        const option = blockOptions[optionIndex];
        if (!isObject(option)) continue;
        if (typeof option.nextScreenId !== 'string') continue;
        pushNavRef({
          source: 'option',
          field: 'option.nextScreenId',
          from,
          declared: option.nextScreenId,
          conditional: true,
          // D6 + решение 22.10.2026: одиночный выбор (blockHonorsOptionNav).
          honoredWhenResolved: blockHonorsOptionNav(block),
          blockId,
          blockType,
          optionId: typeof option.id === 'string' ? option.id : null,
          optionIndex,
        });
      }
    }
  }

  // ── Неявные рёбра по порядку (ступень 4) ─────────────────────────────────
  // ВНИМАНИЕ, унаследованная тонкость (обход WEB-783): заявленной кнопкой
  // «Далее» считается СТРОКА, в том числе пустая. Плеер на ступени 3 смотрит
  // на непустоту и при пустой строке уезжает на следующий экран по порядку,
  // а этот обход в том же случае считает, что автор выход задал. Сохранено
  // как есть: смена поведения — отдельное решение, не фундамент.
  screenEntries.forEach((raw, index) => {
    const blocks = Array.isArray(raw.blocks) ? raw.blocks : [];
    const declaredButtonNext = blocks.some(
      (block) =>
        isObject(block) &&
        block.type === BUTTON_NEXT_BLOCK_TYPE &&
        typeof block.nextScreenId === 'string',
    );
    if (declaredButtonNext || index >= screens.length - 1) return;
    pushNavRef({
      source: 'order',
      field: 'order',
      from: screens[index].id,
      declared: screens[index + 1].id,
      conditional: false,
      honoredWhenResolved: true,
    });
  });

  return {
    screens,
    screenIds,
    blockIds,
    blockOwner,
    screenById,
    screenEntries,
    refs,
    pushNavRef,
  };
}

/** Лёгкий граф посетителя: экраны и ссылки содержимого и порядка. */
export interface FunnelVisitorGraph {
  screens: FunnelGraphScreen[];
  refs: FunnelRef[];
}

/**
 * ЛЁГКИЙ ГРАФ для пути посетителя (WEB-3123, возврат предпроверки по слову
 * владельца 13.10): экраны и ссылки кнопки, вариантов и порядка — без правил,
 * веток, циклов и достижимости. Ровно то, что читает `visitorPathFrom`.
 *
 * Зачем отдельная точка. Плеер лежит в первом экране публичной воронки, а
 * полный граф ему не нужен; вызов `buildFunnelGraph` из плеера затягивал в
 * первый экран весь вычислитель (вес под храповиком
 * `frontend/src/build-guards/publicEntryGuard.ts`). Это не вторая копия:
 * `buildFunnelGraph` строит те же этапы той же `collectFunnelContent`, и
 * ссылки лёгкого графа — ровно начало ссылок полного (тест
 * `funnel-graph.web3123-visitor-graph.spec.ts`).
 */
export function buildFunnelVisitorGraph(
  input: Pick<FunnelGraphInput, 'screens' | 'resolveOptions'>,
): FunnelVisitorGraph {
  if (!Array.isArray(input.screens)) return { screens: [], refs: [] };
  const { screens, refs } = collectFunnelContent(
    input.screens,
    input.resolveOptions,
  );
  return { screens, refs };
}

/**
 * Единственный обход. Никогда не бросает: контент произвольной формы — это
 * норма для jsonb, а обход графа стоит на мягком гейте (предупреждение,
 * НИКОГДА не запрет публикации).
 */
export function buildFunnelGraph(input: FunnelGraphInput): FunnelGraphResult {
  if (!Array.isArray(input.screens)) {
    return emptyResult();
  }
  const rawScreens: unknown[] = input.screens;
  const resolveOptions = input.resolveOptions;
  const maxDepth =
    input.maxConditionGroupDepth ?? DEFAULT_MAX_CONDITION_GROUP_DEPTH;
  const answerSource =
    input.answerConditionSource ?? DEFAULT_ANSWER_CONDITION_SOURCE;
  const gotoTypes = new Set<string>(
    input.gotoActionTypes ?? DEFAULT_GOTO_ACTION_TYPES,
  );

  const {
    screens,
    screenIds,
    blockIds,
    blockOwner,
    screenById,
    screenEntries,
    refs,
    pushNavRef,
  } = collectFunnelContent(rawScreens, resolveOptions);

  // ── Ссылки правил ────────────────────────────────────────────────────────
  function pushLogicRef(
    field: string,
    from: string,
    fromScreenId: string | null,
    declared: unknown,
    ruleId: string | null,
  ): void {
    const resolved =
      typeof declared === 'string' && declared.length > 0
        ? screenIds.has(declared) || blockIds.has(declared)
        : false;
    refs.push({
      source: 'rule',
      field,
      from,
      fromScreenId,
      to: null,
      declared,
      resolved,
      navigational: false,
      conditional: false,
      honoredByPlayer: false,
      blockId: null,
      blockType: null,
      optionId: null,
      optionIndex: null,
      ruleId,
    });
  }

  function walkConditionGroup(
    from: string,
    fromScreenId: string | null,
    ruleId: string | null,
    group: unknown,
    depth: number,
  ): void {
    if (!isObject(group) || depth >= maxDepth) return;
    const items = Array.isArray(group.items) ? group.items : [];
    for (const item of items) {
      if (!isObject(item)) continue;
      if (Array.isArray(item.items)) {
        walkConditionGroup(from, fromScreenId, ruleId, item, depth + 1);
        continue;
      }
      // `source_ref` — id блока ТОЛЬКО у условия по ответу; у URL_PARAM /
      // VARIABLE в этом поле лежит имя параметра или переменной.
      if (item.source !== answerSource) continue;
      if (typeof item.source_ref !== 'string') continue;
      pushLogicRef(
        `Condition.source_ref(${answerSource})`,
        from,
        fromScreenId,
        item.source_ref,
        ruleId,
      );
    }
  }

  /**
   * Может ли группа условий хоть раз оказаться истинной (WEB-3123). Тот же
   * разбор, что у движка правил (`logic-engine/evaluate.ts`): пустая группа
   * не срабатывает никогда (WEB-1255), «все» — нужно каждое, «любое» — хоть
   * одно, неизвестная связка — никогда. Условие по ответу блока, которого в
   * воронке нет, решается так же, как его решит движок без ответа
   * (`answerlessConditionMayFire`): «ответ пуст», «не равно», «не содержит»
   * срабатывают у каждого — путь есть; «равно», числа, «не пуст» — нет.
   */
  function conditionGroupMayFire(group: unknown, depth: number): boolean {
    if (!isObject(group) || depth >= maxDepth) return false;
    const items = Array.isArray(group.items) ? group.items : [];
    if (items.length === 0) return false;
    const itemMayFire = (item: unknown): boolean => {
      if (!isObject(item)) return false;
      if (Array.isArray(item.items)) {
        return conditionGroupMayFire(item, depth + 1);
      }
      if (item.source !== answerSource) return true;
      if (
        typeof item.source_ref === 'string' &&
        blockIds.has(item.source_ref)
      ) {
        return true;
      }
      // Блока нет — ответа не будет никогда; сработает ли условие без ответа,
      // решает движок, копия его решения — `answerlessConditionMayFire`.
      return answerlessConditionMayFire(item);
    };
    if (group.combinator === 'ALL') return items.every(itemMayFire);
    if (group.combinator === 'ANY') return items.some(itemMayFire);
    return false;
  }

  /**
   * Рёбра правил для достижимости (WEB-3123): экран области → экран цели.
   * Только то, что плеер исполнит (`logic-engine/decide.ts`): переход правила
   * плеер ищет среди правил `trigger_scope: 'screen'` с `scope_ref`, равным
   * текущему экрану; цель — существующий экран; условия могут сработать.
   * Область — несуществующий экран или блок: ребро заводится, но обход до
   * такого узла не доходит никогда, так что пути оно не даёт.
   */
  const ruleReachEdges: Array<{ from: string; to: string }> = [];
  /**
   * «Перейти к результату» с целью, которой нет ни кодом, ни именем (WEB-3486):
   * плеер уводит на запасной экран, ребро туда есть, но то, что автор вписал
   * несуществующую цель, остаётся «битой ссылкой» — как было до правки.
   */
  const missingEndingTargets = new Set<FunnelRef>();

  if (Array.isArray(input.rules)) {
    for (const rule of input.rules) {
      if (!isObject(rule)) continue;
      const ruleId = typeof rule.id === 'string' ? rule.id : null;
      const scopeRef =
        typeof rule.scope_ref === 'string' && rule.scope_ref
          ? rule.scope_ref
          : null;
      const from = scopeRef ?? '(logic)';
      const fromScreenId = scopeRef
        ? screenIds.has(scopeRef)
          ? scopeRef
          : (blockOwner.get(scopeRef) ?? null)
        : null;
      if (typeof rule.scope_ref === 'string') {
        // Область правила проверяется от '(logic)': это ссылка САМОГО правила,
        // экрана-владельца у неё ещё нет — он ею и задаётся.
        pushLogicRef('Rule.scope_ref', '(logic)', null, rule.scope_ref, ruleId);
      }
      walkConditionGroup(from, fromScreenId, ruleId, rule.condition_group, 0);
      const action = rule.action;
      // «Перейти к пейволлу воронки» (WEB-3819): переход есть, экрана нет —
      // плеер заканчивает квиз (`fireQuizComplete`), как после последнего
      // экрана. Ни битой ссылки, ни петли, ни ребра достижимости. Ссылка
      // заводится общей дверью `pushNavRef` (цели нет — `to: null`), затем
      // помечается найденной и конечной: этот код едет в первый экран
      // покупателя, каждый байт на счету храповика веса (WEB-1988/WEB-2015).
      if (isObject(action) && action.type === 'GOTO_PAYWALL') {
        Object.assign(
          pushNavRef({
            source: 'rule',
            field: 'Rule.action(GOTO_PAYWALL)',
            from,
            fromScreenId,
            declared: undefined,
            conditional: true,
            honoredWhenResolved: false,
            ruleId,
          }),
          {
            resolved: true,
            honoredByPlayer: fromScreenId !== null,
            toPaywall: true,
          },
        );
        continue;
      }
      // «Перейти к результату» без цели — тоже переход (так его заводит
      // редактор правил): цель и запаски — как у плеера (WEB-3486).
      const isEnding =
        isObject(action) && action.type === GOTO_ENDING_ACTION_TYPE;
      if (
        isObject(action) &&
        typeof action.type === 'string' &&
        gotoTypes.has(action.type) &&
        (typeof action.target === 'string' || isEnding)
      ) {
        // Цель кодом или именем экрана — одна функция для обоих переходов:
        // плеер приводит имя к коду и для «перейти на экран»
        // (`resolveScreenRefs`), иначе граф зовёт экран недостижимым, а
        // ссылку битой (WEB-3491). Не нашлась — null.
        const named =
          typeof action.target === 'string'
            ? findScreenByCodeOrName(screenEntries, action.target)
            : null;
        const ref = pushNavRef({
          source: 'rule',
          field: `Rule.action.target(${action.type})`,
          from,
          fromScreenId,
          declared: action.target,
          conditional: true,
          honoredWhenResolved: fromScreenId !== null,
          ruleId,
          resolvedTo: isEnding
            ? resolveEndingScreenId(screenEntries, named)
            : named,
        });
        // Цель вписана, но такого экрана нет: плеер ушёл на запасной экран,
        // а ссылка — битая. Не строковая цель сюда тоже попадает, но в
        // «битые» её не пускает проверка `declared` ниже — как и раньше.
        if (isEnding && named === null && action.target) {
          missingEndingTargets.add(ref);
        }
        // «К результату» в сам экран правила (экрана результата нет —
        // последний экран): плеер тут завершает квиз (`QuizScreenWizard`,
        // endingIdx === safeIndex → fireQuizComplete). Ссылка найдена, но
        // ребра нет — ни петли, ни «цикла» (WEB-3491). «Перейти на экран»
        // на себя — настоящая петля, её не трогаем.
        if (isEnding && ref.to === fromScreenId) ref.to = null;
        if (
          rule.trigger_scope === 'screen' &&
          scopeRef !== null &&
          ref.to !== null &&
          conditionGroupMayFire(rule.condition_group, 0)
        ) {
          ruleReachEdges.push({ from: scopeRef, to: ref.to });
        }
      }
    }
  }

  // ── Производные ──────────────────────────────────────────────────────────
  const danglingRefs: FunnelDanglingRef[] = [];
  for (const ref of refs) {
    if (ref.source === 'order') continue;
    if (ref.resolved && !missingEndingTargets.has(ref)) continue;
    if (typeof ref.declared !== 'string' || ref.declared.length === 0) continue;
    danglingRefs.push({
      field: ref.field,
      fromScreen: ref.from,
      value: ref.declared,
    });
  }

  const cycles = detectFunnelCycles(
    edgeMapFromRefs(refs.filter((r) => r.navigational && r.source !== 'order')),
  );

  const unreachableScreenIds = findUnreachable(refs, screens, ruleReachEdges);
  const { forks, branches } = analyseBranches(refs, screens, screenById);

  return {
    screens,
    screenIds: screens.map((s) => s.id),
    refs,
    danglingRefs,
    cycles,
    forks,
    branches,
    deadEndBranches: branches.filter((b) => b.isDeadEnd),
    unreachableScreenIds,
  };
}

/**
 * Недостижимые экраны — обход в ширину от первого экрана по САМОМУ ШИРОКОМУ
 * набору навигационных рёбер (кнопка «Далее», переход любого варианта ответа,
 * порядок). Шире плеерского намеренно: предупреждение об осиротевшем экране
 * должно молчать везде, где до экрана хоть как-то можно доехать.
 *
 * WEB-3123: сюда же входят переходы ПРАВИЛ, которые плеер может исполнить
 * (`ruleReachEdges`, отбор — в `buildFunnelGraph`). Раньше правила не входили
 * (наследство обхода WEB-783), и экран, на который ведёт только правило,
 * назывался недостижимым, хотя покупатель на него попадает. Правило, которое
 * не сработает никогда (цель или область — несуществующий экран, область —
 * блок, пустые условия, условие по ответу несуществующего блока), пути не
 * даёт.
 */
function findUnreachable(
  refs: FunnelRef[],
  screens: FunnelGraphScreen[],
  ruleReachEdges: ReadonlyArray<{ from: string; to: string }>,
): string[] {
  if (screens.length === 0) return [];
  const edges = edgeMapFromRefs(
    refs.filter(
      (r) =>
        (r.source === 'button_next' &&
          r.blockType === BUTTON_NEXT_BLOCK_TYPE) ||
        r.source === 'option' ||
        r.source === 'order',
    ),
  );
  for (const edge of ruleReachEdges) {
    if (!edges.has(edge.from)) edges.set(edge.from, new Set());
    edges.get(edge.from)!.add(edge.to);
  }
  const startId = screens[0].id;
  const visited = new Set<string>([startId]);
  const queue = [startId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const next of edges.get(current) ?? []) {
      if (!visited.has(next)) {
        visited.add(next);
        queue.push(next);
      }
    }
  }
  return screens.map((s) => s.id).filter((id) => !visited.has(id));
}

/** Безусловный выход экрана: ступень 3, иначе ступень 4 лестницы плеера. */
function unconditionalNext(
  refs: FunnelRef[],
  screenId: string,
): { to: string; source: FunnelRefSource } | null {
  const button = refs.find(
    (r) =>
      r.from === screenId &&
      r.source === 'button_next' &&
      r.honoredByPlayer &&
      r.to !== null,
  );
  if (button) return { to: button.to!, source: 'button_next' };
  const order = refs.find(
    (r) => r.from === screenId && r.source === 'order' && r.to !== null,
  );
  return order ? { to: order.to!, source: 'order' } : null;
}

/**
 * ПУТЬ ПОСЕТИТЕЛЯ от указанного экрана до конца воронки (WEB-1812).
 *
 * Отдаёт экраны, которые посетитель пройдёт, если дальше никуда не прыгнет:
 * безусловная ступень лестницы плеера (кнопка «Далее» → следующий экран по
 * порядку). Первым элементом идёт САМ стартовый экран, поэтому длина ответа —
 * это «сколько экранов посетителю осталось, считая текущий».
 *
 * ЗАЧЕМ ОТДЕЛЬНАЯ ТОЧКА. Прогресс «шаг N из M» и отметки вовлечённости
 * (quiz50/quiz90) считались по ОБЩЕМУ списку экранов: правило уносило
 * посетителя с экрана 3 на экран 30 из 40 — и он мгновенно числился прошедшим
 * 75 %, не увидев ничего. Знаменатель обязан быть длиной пути ЭТОГО
 * посетителя. Готовые `branches[].screenIds` тут не годятся: плееру нужен путь
 * от ЛЮБОГО экрана, в том числе вне всякой развилки.
 *
 * ПОЧЕМУ ТОЛЬКО БЕЗУСЛОВНАЯ СТУПЕНЬ. Условный переход (правило, вариант
 * ответа) зависит от ответа, которого на текущем экране ещё нет. Пока ответа
 * нет, честная оценка остатка — линия по умолчанию; как только посетитель
 * ответил и правило сработало, плеер зовёт эту же функцию от НОВОГО экрана и
 * знаменатель пересчитывается.
 *
 * ЗАПАСНОЙ ШАГ ПО ПОРЯДКУ — не вторая копия лестницы, а та же оговорка, что у
 * `analyseBranches.nextByOrder`: заявленной кнопкой «Далее» этот обход считает
 * ЛЮБУЮ строку, включая пустую (унаследованная тонкость WEB-783), и ребра
 * порядка у такого экрана не заводит вовсе. Плеер на пустой строке уезжает на
 * следующий экран. Без запасного шага путь обрывался бы на первом же таком
 * экране, знаменатель схлопывался бы в единицу — и КАЖДЫЙ посетитель считался
 * бы прошедшим воронку целиком. Это ровно та ложь, которую карточка лечит.
 *
 * Петля пути не зацикливает: экран, уже попавший в путь, обрывает обход.
 */
export function visitorPathFrom(
  graph: FunnelVisitorGraph,
  startScreenId: string,
): string[] {
  const byId = new Map<string, FunnelGraphScreen>();
  for (const screen of graph.screens) {
    if (!byId.has(screen.id)) byId.set(screen.id, screen);
  }
  if (!byId.has(startScreenId)) return [];

  const path: string[] = [];
  const visited = new Set<string>();
  let current: string | null = startScreenId;
  while (current !== null && !visited.has(current)) {
    visited.add(current);
    path.push(current);
    const step = unconditionalNext(graph.refs, current);
    if (step !== null) {
      current = step.to;
      continue;
    }
    const index: number | undefined = byId.get(current)?.index;
    const byOrder: string | null =
      index === undefined ? null : (graph.screens[index + 1]?.id ?? null);
    current = byOrder;
  }
  return path;
}

/**
 * Ступени 1-3 лестницы плеера ПО СТАРШИНСТВУ — тот же порядок, которым решает
 * переход сам плеер (`decideTransition`: правило → переход варианта → кнопка
 * «Далее» → порядок). Ступень 4 (порядок) сюда не входит: она не решение
 * автора, а умолчание; легаси-`branch` плеер не читает вовсе.
 */
const EXPLICIT_EXIT_LADDER: FunnelRefSource[] = [
  'rule',
  'option',
  'button_next',
];

/**
 * Собственный (явный) переход экрана — ступени 1-3 лестницы плеера.
 *
 * РЕВИЗИЯ ПАЧКИ 8: ступени перебираются по СТАРШИНСТВУ, а не в порядке
 * массива ссылок. Порядок сборки ссылок обратен лестнице (кнопки собираются
 * раньше вариантов, правила — последними), поэтому поиск «первой подходящей»
 * отвечал «кнопка» там, где плеер пойдёт по правилу: у экрана с обоими
 * переходами ветка уводилась туда, куда пользователь не попадёт, и пересказ
 * называл чужой экран.
 */
function explicitScreenExit(
  refs: FunnelRef[],
  screenId: string,
): { to: string; source: FunnelRefSource } | null {
  for (const source of EXPLICIT_EXIT_LADDER) {
    const own = refs.find(
      (r) =>
        (r.fromScreenId ?? r.from) === screenId &&
        r.honoredByPlayer &&
        r.to !== null &&
        r.source === source,
    );
    if (own) return { to: own.to!, source };
  }
  return null;
}

/**
 * Свой выход экрана, который ВЫВОДИТ из уже пройденного хвоста ветки.
 *
 * РЕВИЗИЯ ПАЧКИ 8. Правило «ветка кончается на экране со своим переходом» не
 * спрашивало, КУДА этот переход ведёт. Правило-петля (`s6 → s6`) или возврат
 * на собственную голову (`s9 → s8`) объявляли выходом ветки экран самой
 * ветки, и пересказ выдавал бессмыслицу «экраны 5-6, затем «Экран 6»». Выход
 * — это уход ИЗ набора экранов ветки, поэтому переход внутрь уже пройденного
 * хвоста выходом не считается, и хвост идёт дальше по безусловной ступени —
 * ровно как до появления правила (поведение до 18030a48).
 */
function branchExitFrom(
  refs: FunnelRef[],
  screenId: string,
  visited: ReadonlySet<string>,
): { to: string; source: FunnelRefSource } | null {
  const own = explicitScreenExit(refs, screenId);
  return own && !visited.has(own.to) ? own : null;
}

/**
 * Развилки, ветки и тупики.
 *
 * Ветка = хвост от цели условного перехода развилки до ВЫХОДА: до слияния
 * (общего экрана) или до конца воронки. Идёт хвост по безусловной ступени
 * лестницы (кнопка «Далее», иначе порядок), но КОНЧАЕТСЯ на первом экране, у
 * которого есть собственный переход в сторону (`explicitScreenExit`): именно
 * им рецепт ветки (WEB-1808) выводит ветку на слияние — «правило с повтором
 * условия входа» либо `button_next.nextScreenId` последнего экрана. Переход,
 * ведущий ровно туда же, куда ведёт безусловная ступень, выходом не считаем:
 * это та же линия, а не уход из ветки.
 *
 * ШРАМ (живой отказ 02.09, WEB-1808/1809). Раньше хвост шёл ТОЛЬКО по
 * безусловной ступени и своего выхода экрана не замечал: ветка тянулась по
 * порядку до конца воронки и забирала чужие экраны. Пересказ чата говорил
 * «затем экран 8» и «экраны 8-20» там, где обе ветки возвращались на общий
 * экран 15, и плашка веток превью врала той же длиной — два места, один
 * источник.
 *
 * Принадлежность экрана ветке дополнительно считается по БЛИЖАЙШЕЙ голове:
 * экран, до которого от двух голов одинаково далеко, не принадлежит никому —
 * это и есть слияние (случай, когда явного выхода никто не поставил). Тупик =
 * хвост, у последнего экрана которого нет ни одного явного выхода, и он
 * проваливается ПО ПОРЯДКУ в экран, принадлежащий СОСЕДНЕЙ ветке той же
 * развилки.
 */
function analyseBranches(
  refs: FunnelRef[],
  screens: FunnelGraphScreen[],
  screenById: Map<string, FunnelGraphScreen>,
): { forks: FunnelFork[]; branches: FunnelBranch[] } {
  const conditionalTargets = new Map<string, string[]>();
  for (const ref of refs) {
    if (!ref.conditional || !ref.honoredByPlayer || ref.to === null) continue;
    const owner = ref.fromScreenId;
    if (owner === null) continue;
    const targets = conditionalTargets.get(owner) ?? [];
    if (!targets.includes(ref.to)) targets.push(ref.to);
    conditionalTargets.set(owner, targets);
  }

  const forkScreenIds = new Set<string>();
  for (const screen of screens) {
    const targets = conditionalTargets.get(screen.id);
    if (targets && targets.length >= 2) forkScreenIds.add(screen.id);
  }

  /** Следующий экран ПО ПОРЯДКУ (ступень 4), даже если ребра порядка нет. */
  const nextByOrder = (screenId: string): string | null => {
    const index = screenById.get(screenId)?.index;
    if (index === undefined) return null;
    return screens[index + 1]?.id ?? null;
  };

  const forks: FunnelFork[] = [];
  const branches: FunnelBranch[] = [];

  for (const screen of screens) {
    if (!forkScreenIds.has(screen.id)) continue;
    const heads = conditionalTargets.get(screen.id) ?? [];

    // Проход А — куда путь каждой ветки уводит ВООБЩЕ (по своим выходам и
    // безусловной ступени). Нужен, чтобы отличить слияние от продолжения
    // линии: экран, до которого добирается и соседняя ветка, — общий. Путь
    // обрывается на голове СОСЕДНЕЙ ветки: дальше это уже её путь, а не наш.
    const headSet = new Set(heads);
    const reachOf = new Map<string, Set<string>>();
    for (const head of heads) {
      const seen = new Set<string>([head]);
      let current = head;
      for (;;) {
        if (forkScreenIds.has(current)) break;
        const step =
          branchExitFrom(refs, current, seen) ??
          unconditionalNext(refs, current);
        if (!step || seen.has(step.to) || headSet.has(step.to)) break;
        seen.add(step.to);
        current = step.to;
      }
      reachOf.set(head, seen);
    }
    const sharedWithOtherBranch = (id: string, head: string): boolean =>
      heads.some(
        (other) => other !== head && (reachOf.get(other)?.has(id) ?? false),
      );

    // Сырые цепочки от каждой головы: идём по безусловной ступени лестницы,
    // останавливаемся на экране со СВОИМ выходом (он же — выход ветки).
    type Chain = {
      ids: string[];
      exit: { to: string; source: FunnelRefSource } | null;
    };
    const chains = new Map<string, Chain>();
    for (const head of heads) {
      const ids: string[] = [head];
      const seen = new Set<string>([head]);
      let current = head;
      let exit: { to: string; source: FunnelRefSource } | null = null;
      for (;;) {
        if (forkScreenIds.has(current)) break; // вложенная развилка
        const own = branchExitFrom(refs, current, seen);
        // Свой переход НА СЛЕДУЮЩИЙ ПО ПОРЯДКУ экран — та же линия ветки
        // (автор просто проставил кнопку «Далее» подряд), а не уход из неё;
        // но если туда же приходит и соседняя ветка — это слияние, и ветка
        // на нём кончается.
        if (
          own &&
          (own.to !== nextByOrder(current) ||
            sharedWithOtherBranch(own.to, head))
        ) {
          exit = own;
          break;
        }
        const next = own ?? unconditionalNext(refs, current);
        if (!next || seen.has(next.to)) break;
        ids.push(next.to);
        seen.add(next.to);
        current = next.to;
      }
      chains.set(head, { ids, exit });
    }

    // Владелец экрана = единственная ближайшая голова; ничья = слияние.
    const distance = new Map<string, Map<string, number>>();
    for (const head of heads) {
      (chains.get(head)?.ids ?? []).forEach((id, step) => {
        const perHead = distance.get(id) ?? new Map<string, number>();
        if (!perHead.has(head)) perHead.set(head, step);
        distance.set(id, perHead);
      });
    }
    const ownerOf = (id: string): string | null => {
      const perHead = distance.get(id);
      if (!perHead) return null;
      let best: string | null = null;
      let bestStep = Number.POSITIVE_INFINITY;
      let tie = false;
      for (const [head, step] of perHead) {
        if (step < bestStep) {
          best = head;
          bestStep = step;
          tie = false;
        } else if (step === bestStep) {
          tie = true;
        }
      }
      return tie ? null : best;
    };

    const branchIds: string[] = [];
    for (const head of heads) {
      const chain: Chain = chains.get(head) ?? { ids: [head], exit: null };
      const screenIds: string[] = [];
      // Чужой экран в цепочке — слияние: до него ветка не доходит, и он
      // старше собственного выхода (свой выход просто не понадобился).
      let mergeScreenId: string | null = null;
      for (const id of chain.ids) {
        if (ownerOf(id) === head) {
          screenIds.push(id);
          continue;
        }
        mergeScreenId = id;
        break;
      }
      const last = screenIds[screenIds.length - 1] ?? head;
      const merged = mergeScreenId !== null;
      const fallThrough = merged ? unconditionalNext(refs, last) : null;
      const exitScreenId = merged ? mergeScreenId : (chain.exit?.to ?? null);
      const exitSource = merged
        ? (fallThrough?.source ?? null)
        : (chain.exit?.source ?? null);
      // Уход к пейволлу воронки (WEB-3819) — тоже свой выход: ветка
      // кончается концом квиза, а не проваливается в соседнюю. У такой
      // ссылки «плеер исполнит» ровно «экран правила есть» (`fromScreenId`
      // не null), поэтому совпадение экрана правила с `last` его и судит.
      const hasExplicitExit =
        explicitScreenExit(refs, last) !== null ||
        refs.some((r) => r.toPaywall && r.fromScreenId === last);
      const fallsInto =
        exitScreenId === null ? null : (ownerOf(exitScreenId) ?? null);
      const isDeadEnd =
        merged &&
        exitSource === 'order' &&
        !hasExplicitExit &&
        fallsInto !== null &&
        fallsInto !== head;
      branchIds.push(head);
      branches.push({
        id: head,
        forkScreenId: screen.id,
        name: screenById.get(head)?.name ?? head,
        screenIds,
        exitScreenId,
        exitSource,
        isDeadEnd,
        fallsIntoBranchId: isDeadEnd ? fallsInto : null,
      });
    }

    forks.push({ screenId: screen.id, targets: heads, branchIds });
  }

  return { forks, branches };
}
